export const CIRCLE_POINTS = 64
export const DEGREES_IN_CIRCLE = 360
export const DEGREES_FOR_PI = 180
export const METERS_PER_DEGREE = 111320

export function createMarkerElement(role: string, color: string): HTMLDivElement {
  const el = document.createElement('div')
  el.dataset.markerRole = role
  el.style.width = '16px'
  el.style.height = '16px'
  el.style.borderRadius = '50%'
  el.style.backgroundColor = color
  el.style.border = '2px solid #FFFFFF'
  el.style.boxShadow = '0 0 2px rgba(0, 0, 0, 0.3)'
  return el
}

// Convert meters to approximate degrees.
// Note: This uses a simplified spherical approximation and is intended for local geofences.
// For best results, use for distances up to ~50 km from the center point (error typically < 1–2%).
// Do not use for continent-scale geofences, routing, or other high-precision geodesic calculations.
function metersToDegrees(meters: number, latitude: number = 0): number {
  const metersPerDegree = METERS_PER_DEGREE * Math.cos((latitude * Math.PI) / DEGREES_FOR_PI)
  return meters / metersPerDegree
}

/**
 * Convert center + width/height (meters) to 4 corner coordinates in GeoJSON order [lng, lat].
 * Used for polygon geofence API payload (rectangle shape).
 */
export function rectangleToPolygonCoordinates(
  centerLng: number,
  centerLat: number,
  widthMeters: number,
  heightMeters: number
): [number, number][] {
  const halfWidth = metersToDegrees(widthMeters / 2, centerLat)
  const halfHeight = heightMeters / 2 / METERS_PER_DEGREE
  return [
    [centerLng - halfWidth, centerLat - halfHeight],
    [centerLng + halfWidth, centerLat - halfHeight],
    [centerLng + halfWidth, centerLat + halfHeight],
    [centerLng - halfWidth, centerLat + halfHeight]
  ]
}

/**
 * Convert 4 corner coordinates (GeoJSON [lng, lat]) to center and width/height in meters.
 * Used to load API polygon geofence into UI form.
 */
export function polygonCoordinatesToRectangle(
  coords: [number, number][]
): { centerLng: number; centerLat: number; widthMeters: number; heightMeters: number } | null {
  if (!coords || coords.length < 4) return null
  const lngs = coords.slice(0, 4).map((c) => c[0])
  const lats = coords.slice(0, 4).map((c) => c[1])
  const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2
  const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2
  const widthDeg = Math.max(...lngs) - Math.min(...lngs)
  const heightDeg = Math.max(...lats) - Math.min(...lats)
  const widthMeters = Math.abs(widthDeg * METERS_PER_DEGREE * Math.cos((centerLat * Math.PI) / DEGREES_FOR_PI))
  const heightMeters = Math.abs(heightDeg * METERS_PER_DEGREE)
  return { centerLng, centerLat, widthMeters, heightMeters }
}

// Create circle GeoJSON
export function createCircleGeoJSON(
  lng: number,
  lat: number,
  radiusMeters: number
): GeoJSON.Feature<GeoJSON.Polygon> {
  const points = CIRCLE_POINTS
  const coords: [number, number][] = []
  const distanceX = metersToDegrees(radiusMeters, lat)
  const distanceY = radiusMeters / METERS_PER_DEGREE // meters to degrees latitude

  for (let i = 0; i < points; i++) {
    const angle = (i * DEGREES_IN_CIRCLE) / points
    const dx = distanceX * Math.cos((angle * Math.PI) / DEGREES_FOR_PI)
    const dy = distanceY * Math.sin((angle * Math.PI) / DEGREES_FOR_PI)
    coords.push([lng + dx, lat + dy])
  }
  coords.push(coords[0]) // Close the circle

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coords]
    },
    properties: {}
  }
}

// Create rectangle GeoJSON
export function createRectangleGeoJSON(
  lng: number,
  lat: number,
  widthMeters: number,
  heightMeters: number
): GeoJSON.Feature<GeoJSON.Polygon> {
  const halfWidth = metersToDegrees(widthMeters / 2, lat)
  const halfHeight = heightMeters / 2 / METERS_PER_DEGREE

  const coords: [number, number][] = [
    [lng - halfWidth, lat - halfHeight],
    [lng + halfWidth, lat - halfHeight],
    [lng + halfWidth, lat + halfHeight],
    [lng - halfWidth, lat + halfHeight],
    [lng - halfWidth, lat - halfHeight]
  ]

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coords]
    },
    properties: {}
  }
}
